import React, { useEffect, useMemo, useState } from "react";

import { useSelector, useDispatch } from "react-redux";

import {
  ArrowPathIcon,
  DocumentArrowDownIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import TableComponent from "../../components/TableComponent";

import { toast } from "react-toastify";

import * as ExcelJS from "exceljs";

import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

import ConfirmModal from "../../components/ConfirmModal";

import {
  deleteRegistration,
  fetchRegistration,
} from "../../features/admin/RegistrationSlice";

import ApiCustom from "../../utils/ApiCustom";

const formatVND = (number) => {
  if (!number) return "";

  const n =
    typeof number === "string"
      ? Number(number.toString().replace(/\D/g, ""))
      : number;

  if (!n) return "";

  return n.toLocaleString("vi-VN") + "₫";
};

const getAmountNumber = (amount) => {
  if (!amount) return 0;

  return Number(amount.toString().replace(/[^\d]/g, ""));
};

const getShortText = (text, maxLength = 60) => {
  if (!text) return "";

  const value = String(text);

  if (value.length <= maxLength) return value;

  return `${value.slice(0, maxLength).trim()}...`;
};

const Registration = () => {
  const dispatch = useDispatch();

  const { registrations, status, error } = useSelector(
    (state) => state.registrations,
  );

  const { partners } = useSelector((state) => state.partners);

  const [deleteId, setDeleteId] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [selectedActivityType, setSelectedActivityType] = useState("");

  const [searchValue, setSearchValue] = useState("");

  const [isOpenPayment, setIsOpenPayment] = useState(false);

  const [paymentId, setPaymentId] = useState(null);

  const [statusType, setStatusType] = useState("");

  const [confirmContent, setConfirmContent] = useState("");

  const [amountMap, setAmountMap] = useState({});

  const getCompanyName = (item) => {
    return (
      partners?.find((p) => p.id === item.company?.id)?.companyName ||
      item.organizationName ||
      ""
    );
  };

  const hasPermission = (requiredPermission) => {
    const userId = localStorage.getItem("userId");

    if (userId == 1) return true;

    const permissions = localStorage.getItem("permissions");

    const userPermissions = permissions
      ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
      : [];

    return userPermissions.includes(requiredPermission);
  };

  const handleClosePayment = () => {
    setIsOpenPayment(false);

    setPaymentId(null);

    setStatusType("");

    setConfirmContent("");
  };

  const handlePayment = async () => {
    if (statusType === "payment") {
      const amt = getAmountNumber(amountMap[paymentId]);

      if (!amt || amt <= 0) {
        toast.error(
          "Vui lòng nhập số tiền hợp lệ trước khi xác nhận đóng phí!",
        );

        handleClosePayment();

        return;
      }

      await handleUpdateValue(paymentId, "payment", { amount: amt });

      handleClosePayment();

      return;
    }

    await handleUpdateValue(paymentId, statusType);

    handleClosePayment();
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);

    setIsDeleteOpen(false);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteRegistration(deleteId)).unwrap();

      toast.success("Xóa đăng ký thành công!");

      handleCloseDeleteModal();
    } catch {
      toast.error("Đã xảy ra lỗi khi xóa đăng ký!");
    }
  };

  const exportToExcel = async () => {
    const dataToExport = filteredRegistrations;

    const workbook = new ExcelJS.Workbook();

    const worksheet = workbook.addWorksheet("Danh sách đăng ký");

    worksheet.columns = [
      { header: "STT", key: "index", width: 6 },
      { header: "Người tham gia", key: "fullName", width: 22 },
      { header: "Số điện thoại", key: "phone", width: 18 },
      { header: "Email", key: "email", width: 26 },
      { header: "Đơn vị", key: "companyName", width: 35 },
      { header: "Địa chỉ Doanh nghiệp", key: "businessAddress", width: 40 },
      { header: "Ngành nghề kinh doanh", key: "businessField", width: 28 },
      { header: "Hoạt động", key: "postTitle", width: 45 },
      { header: "Trạng thái đóng phí", key: "payment", width: 20 },
      { header: "Số tiền đã đóng", key: "amount", width: 18 },
      { header: "Tham gia", key: "status", width: 12 },
    ];

    const rows = dataToExport.map((item, index) => {
      const companyName = getCompanyName(item);

      const amount =
        amountMap[item.id] && getAmountNumber(amountMap[item.id]) > 0
          ? getAmountNumber(amountMap[item.id])
          : item.amount || 0;

      return {
        index: index + 1,
        fullName: item.fullName || "",
        phone: item.phoneNumber || "",
        email: item.email || "",
        companyName,
        businessAddress: item.businessAddress || "",
        businessField: item.businessField || "",
        postTitle: item.post?.title || "",
        payment: item.payment || "",
        amount,
        status: item.status || "",
      };
    });

    worksheet.addRows(rows);

    const headerRow = worksheet.getRow(1);

    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",

        pattern: "solid",

        fgColor: { argb: "FFE0E0E0" },
      };

      cell.font = { bold: true, color: { argb: "FF000000" } };

      cell.border = {
        top: { style: "thin" },

        left: { style: "thin" },

        bottom: { style: "thin" },

        right: { style: "thin" },
      };
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },

            left: { style: "thin" },

            bottom: { style: "thin" },

            right: { style: "thin" },
          };

          cell.alignment = { wrapText: true };
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");

    a.href = url;

    a.download = `Danh_sach_dang_ky_${selectedActivityType || "tat_ca"}.xlsx`;

    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleUpdateValue = async (id, field, payload = {}) => {
    const endpoint =
      field === "payment"
        ? `eventRegistration-pay/${id}`
        : `eventRegistration-change-field/${id}?t=${field}`;

    ApiCustom.put(endpoint, payload, {
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => {
        if (res.status === 200) {
          toast.success("Cập nhật thành công");

          dispatch(fetchRegistration());
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.messages || "Có lỗi xảy ra");
      });
  };

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchRegistration());
    }
  }, [status, dispatch]);

  useEffect(() => {
    dispatch(fetchRegistration());
  }, [dispatch]);

  useEffect(() => {
    const map = {};

    registrations?.forEach((r) => {
      map[r.id] = formatVND(r.amount);
    });

    setAmountMap(map);
  }, [registrations]);

  const activityTypes = [
    ...new Set(
      registrations?.map((reg) => reg.post?.title || "Chưa phân loại"),
    ),
  ];

  const handleAmountChange = (id, value) => {
    const raw = value.replace(/[^\d]/g, "");

    const formatted = raw ? Number(raw).toLocaleString("vi-VN") + "₫" : "";

    setAmountMap((prev) => ({ ...prev, [id]: formatted }));
  };

  const headers = [
    {
      key: "id",

      label: "STT",

      render: (item, index) => (
        <div>
          <p className="font-medium">{index + 1}</p>
        </div>
      ),
    },

    {
      key: "fullName",

      label: "Thông tin người tham gia",

      render: (item) => (
        <div className="min-w-[220px] whitespace-normal break-words">
          <p className="font-semibold text-gray-800 leading-5">
            {item?.fullName || ""}
          </p>
          <p className="text-sm text-gray-600 mt-1 leading-5 whitespace-normal break-words">
            {[item?.phoneNumber, item?.email].filter(Boolean).join(" ")}
          </p>
        </div>
      ),
    },

    {
      key: "companyName",

      label: "Đơn vị",

      render: (item) => {
        const companyName = getCompanyName(item);

        return (
          <div className="min-w-[220px] max-w-[300px] whitespace-normal break-words">
            <p className="font-semibold text-gray-800 leading-5">
              {companyName}
            </p>

            {item.businessAddress && (
              <p className="text-sm text-gray-600 mt-1 leading-5 whitespace-normal break-words">
                {item.businessAddress}
              </p>
            )}
          </div>
        );
      },
    },

    {
      key: "businessField",

      label: "Ngành nghề",

      render: (item) => (
        <div className="min-w-[160px] max-w-[220px] whitespace-normal break-words">
          <p className="font-medium text-gray-700 leading-5 whitespace-normal break-words">
            {item.businessField || ""}
          </p>
        </div>
      ),
    },

    {
      key: "post",

      label: "Hoạt động",

      render: (item) => {
        const title = item.post ? item.post?.title : "";

        return (
          <div
            className="w-[240px] md:w-[300px] whitespace-normal break-words"
            title={title}
          >
            <p
              className="font-medium leading-5 overflow-hidden"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {title}
            </p>
          </div>
        );
      },
    },

    {
      key: "amount",

      label: "Số tiền đã đóng",

      render: (item) => (
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9,\.]*"
          className="input input-bordered w-40 md:w-48"
          value={amountMap[item.id] || ""}
          onChange={(e) => handleAmountChange(item.id, e.target.value)}
          placeholder="Nhập số tiền"
          disabled={item.payment === "Đã đóng"}
        />
      ),
    },

    {
      key: "payment",

      label: "Trạng thái đóng phí",

      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.payment === "Đã đóng"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          } whitespace-nowrap`}
        >
          {item.payment}
        </span>
      ),
    },

    {
      key: "status",

      label: "Tham gia",

      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.status === "Có"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          } whitespace-nowrap`}
        >
          {item.status}
        </span>
      ),
    },
  ];

  if (hasPermission("REGISTER_UPDATE")) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("REGISTER_UPDATE") && (
          <button
            disabled={item.payment === "Đã đóng"}
            onClick={() => {
              setIsOpenPayment(true);

              setPaymentId(item.id);

              setStatusType("payment");

              setConfirmContent("Xác nhận đóng phí tham gia?");
            }}
            title="Đóng phí tham gia"
            className="btn btn-outline btn-success btn-sm px-3 my-1 rounded"
          >
            <CurrencyDollarIcon className="w-5 h-5" />
          </button>
        )}

        {hasPermission("REGISTER_UPDATE") && (
          <button
            disabled={item.status === "Có"}
            onClick={() => {
              setIsOpenPayment(true);

              setPaymentId(item.id);

              setStatusType("status");

              setConfirmContent("Xác nhận có tham gia?");
            }}
            title="Tham gia"
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <CheckCircleIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  const filteredRegistrations = useMemo(() => {
    let list = registrations || [];

    if (selectedActivityType) {
      list = list.filter(
        (reg) => (reg.post?.title || "Chưa phân loại") === selectedActivityType,
      );
    }

    if (searchValue.trim()) {
      const q = searchValue.toLowerCase();

      list = list.filter((item) =>
        [
          item.fullName,
          item.email,
          item.phoneNumber,
          item.organizationName,
          item.businessAddress,
          item.businessField,
          item.post?.title,
          getCompanyName(item),
        ].some((field) => field?.toLowerCase().includes(q)),
      );
    }

    return list;
  }, [registrations, selectedActivityType, searchValue, partners]);

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-4 text-red-500 bg-red-50 rounded-md">
        Đã xảy ra lỗi: {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="block md:flex justify-between items-center mb-3">
        <h1 className="text-2xl font-bold">
          Quản lý đăng ký tham gia hoạt động
        </h1>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-6">
        <div className="w-full md:max-w-xl">
          <div className="flex items-center gap-2">
            <label
              htmlFor="userSearchFilter"
              className="text-sm font-medium w-24 shrink-0"
            >
              Tìm kiếm:
            </label>

            <input
              id="userSearchFilter"
              type="search"
              className="input input-bordered flex-1 max-h-10 max-w-80"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Nhập tên, email, SĐT hoặc đơn vị"
            />
          </div>
        </div>

        <div className="w-full md:w-auto md:ml-auto">
          <div className="flex flex-wrap gap-2 md:justify-end">
            <select
              className="select select-bordered select-sm w-full md:w-72"
              value={selectedActivityType}
              onChange={(e) => setSelectedActivityType(e.target.value)}
              title={selectedActivityType}
            >
              <option value="">Tất cả hoạt động</option>

              {activityTypes.map((type) => (
                <option key={type} value={type}>
                  {getShortText(type, 65)}
                </option>
              ))}
            </select>

            <button
              onClick={exportToExcel}
              className="btn btn-success btn-outline btn-sm w-full md:w-40"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
              Xuất Excel
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg shadow overflow-x-auto">
        <TableComponent
          headers={headers}
          data={filteredRegistrations}
          settings={tableSettings}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />

      <ConfirmModal
        isOpen={isOpenPayment}
        onClose={handleClosePayment}
        onSubmit={handlePayment}
        message={confirmContent}
      />
    </div>
  );
};

export default Registration;